import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"

import { DialogContent } from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

type DiscardGuardOptions = {
  /**
   * Whether the form has unsaved changes. Read it reactively:
   * `useStore(form.store, (s) => s.isDirty)`. Reading `form.state.isDirty`
   * directly does not re-render, so the guard would check a stale value and
   * let a filled-in form close anyway.
   */
  isDirty: boolean
  /** Runs when the user confirms they want to lose the changes. */
  onDiscard: () => void
}

/**
 * Holds a close attempt while the form is dirty and asks before discarding.
 *
 * `holdClose` returns true when it held the close and opened the
 * confirmation. It returns false when the form is clean, and the caller then
 * closes as usual. `confirmation` is the dialog to render next to the form.
 */
const useDiscardGuard = ({ isDirty, onDiscard }: DiscardGuardOptions) => {
  const [confirmOpen, setConfirmOpen] = React.useState(false)

  const holdClose = () => {
    if (!isDirty) return false
    setConfirmOpen(true)
    return true
  }

  const handleDiscard = () => {
    setConfirmOpen(false)
    onDiscard()
  }

  const confirmation = (
    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Descartar cambios?</AlertDialogTitle>
          <AlertDialogDescription>
            Tienes cambios sin guardar. Si cierras ahora, perderás los datos
            ingresados.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Seguir editando</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDiscard}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Descartar cambios
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  return { holdClose, confirmation }
}

type FormDialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogContent
> &
  DiscardGuardOptions

/**
 * A DialogContent that asks before throwing away a filled-in form.
 *
 * Radix closes a dialog on outside click and on Escape, and BrainHub dialogs
 * call `form.reset()` when they close. Brushing the dimmed backdrop would wipe
 * everything the user typed, with no warning and no way back.
 *
 * While the form is dirty, both gestures are intercepted and a confirmation
 * shows instead. While it is clean, they close immediately. The X, Cancel and
 * submit close without asking.
 *
 * A form that must also ask on the X or on Cancel uses `useDiscardGuard` on
 * the root's `onOpenChange`, which receives every close gesture.
 */
const FormDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  FormDialogContentProps
>(({ isDirty, onDiscard, children, ...props }, ref) => {
  const { holdClose, confirmation } = useDiscardGuard({ isDirty, onDiscard })

  const guard = (event: { preventDefault: () => void }) => {
    if (holdClose()) event.preventDefault()
  }

  return (
    <>
      <DialogContent
        ref={ref}
        onInteractOutside={guard}
        onEscapeKeyDown={guard}
        {...props}
      >
        {children}
      </DialogContent>

      {confirmation}
    </>
  )
})
FormDialogContent.displayName = "FormDialogContent"

export { FormDialogContent, useDiscardGuard }
